import { formatMoney } from "@vouch/shared";
import { ArrowDownCircle, ArrowLeftRight, ArrowUpCircle, Check, X } from "lucide-react";
import { useState } from "react";
import { QuickAddModal } from "../components/QuickAddModal";
import { PeriodFilter } from "../components/PeriodFilter";
import { Button, Card, Spinner } from "../components/ui";
import { confirm } from "../lib/confirm";
import {
  useAccounts,
  useConfirmTransaction,
  useSkipTransaction,
  useTransactions,
  type Period,
  type Transaction,
} from "../lib/hooks";
import { toast } from "../lib/toast";

/** Human description of where money moved for a transaction row. */
function describe(t: Transaction): string {
  const parts: string[] = [];
  if (t.categoryName) parts.push(t.categoryName);
  switch (t.type) {
    case "transfer":
      parts.push(`${t.senderAccountName ?? "?"} → ${t.receiverAccountName ?? "?"}`);
      break;
    case "income":
      if (t.receiverAccountName) parts.push(`to ${t.receiverAccountName}`);
      break;
    case "expense":
      if (t.senderAccountName) parts.push(`from ${t.senderAccountName}`);
      break;
    case "loan_disburse":
      parts.push(`${t.senderUserName ?? "lender"} → ${t.receiverUserName ?? "borrower"}`);
      break;
    case "loan_repayment":
      parts.push("repayment");
      break;
    default:
      break;
  }
  return parts.join(" · ");
}

export function TransactionsPage(): JSX.Element {
  const now = new Date();
  const [period, setPeriod] = useState<Period>({
    month: now.getUTCMonth() + 1,
    year: now.getUTCFullYear(),
  });
  const [accountId, setAccountId] = useState("");
  const { data: accounts = [] } = useAccounts();
  const { data: transactions, isLoading } = useTransactions(period, accountId || undefined);
  const [modal, setModal] = useState<null | "income" | "expense" | "transfer">(null);
  const confirmTx = useConfirmTransaction();
  const skipTx = useSkipTransaction();

  const markDone = async (t: Transaction): Promise<void> => {
    const ok = await confirm({
      title: "Mark as done?",
      message: `This will post ${formatMoney(t.amount, t.currency)} to your ledger.`,
      confirmLabel: "Mark done",
    });
    if (!ok) return;
    await confirmTx
      .mutateAsync({ id: t.id })
      .then(() => toast.success("Posted"))
      .catch((e) => toast.error(e instanceof Error ? e.message : "Failed"));
  };

  const skip = async (t: Transaction): Promise<void> => {
    const ok = await confirm({ title: "Skip this occurrence?", confirmLabel: "Skip", danger: true });
    if (!ok) return;
    await skipTx.mutateAsync(t.id).then(() => toast.success("Skipped"));
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-brand-500">Transactions</h1>
        <div className="flex flex-wrap items-center gap-2">
          <PeriodFilter value={period} onChange={setPeriod} />
          <select
            className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            <option value="">All accounts</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <Button variant="ghost" onClick={() => setModal("income")}>
            <ArrowUpCircle size={16} /> Income
          </Button>
          <Button variant="ghost" onClick={() => setModal("expense")}>
            <ArrowDownCircle size={16} /> Expense
          </Button>
          <Button onClick={() => setModal("transfer")}>
            <ArrowLeftRight size={16} /> Transfer
          </Button>
        </div>
      </div>

      <Card>
        {transactions && transactions.length > 0 ? (
          <ul className="divide-y divide-slate-100">
            {transactions.map((t) => {
              const pending = t.status === "pending";
              const sub = describe(t);
              return (
                <li key={t.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-700">
                      {t.description || t.type.replace("_", " ")}
                      {pending && (
                        <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-700">
                          pending
                        </span>
                      )}
                    </p>
                    <p className="truncate text-xs text-slate-400">
                      {new Date(t.occurredAt).toLocaleDateString()} · {t.type.replace("_", " ")}
                      {sub && ` · ${sub}`}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {pending && t.recurringRuleId && (
                      <>
                        <Button variant="ghost" onClick={() => void markDone(t)}>
                          <Check size={15} /> Done
                        </Button>
                        <Button variant="ghost" onClick={() => void skip(t)}>
                          <X size={15} />
                        </Button>
                      </>
                    )}
                    <span
                      className={
                        pending
                          ? "text-sm font-semibold text-slate-400"
                          : t.type === "income"
                            ? "text-sm font-semibold text-green-600"
                            : t.type === "expense"
                              ? "text-sm font-semibold text-red-600"
                              : "text-sm font-semibold text-slate-600"
                      }
                    >
                      {formatMoney(t.amount, t.currency)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">No transactions in this period.</p>
        )}
      </Card>

      <QuickAddModal open={modal !== null} mode={modal ?? "expense"} onClose={() => setModal(null)} />
    </div>
  );
}
